import React, { useRef } from "react";
import { CLOSE_REASONS } from "../rounds/LoomSheet";
import { fmtDay } from "../rates/rateModel";
import type { SheetLine } from "../sheet/sheetModel";
import { useKT } from "../i18n/kt";
import { dayMonth, DailyRow, needsPart, needsPeople } from "./dailyModel";

const reasonLabel = (k?: string) => CLOSE_REASONS.find((r) => r.key === k)?.label || "Closed";
const LONG_PRESS_MS = 500;

export type ListProps = {
  rows: DailyRow[];
  lines: Map<string, SheetLine>;
  date: string;
  partName: (workTypeId?: string) => string;
  /** Today's weaver when changed on this screen (the row shows them, not the saree's usual one). */
  personOf?: (id?: string) => { code: string; name?: string } | null;
  selectedId?: string | null;
  onType: (loomId: string, inches: string) => void;
  onPaste: (loomId: string, text: string) => boolean;
  onEnter: (loomId: string) => void;
  registerInput: (loomId: string, el: HTMLInputElement | null) => void;
  /** The loom number: open the loom (its page, or the right pane on a tablet). */
  onOpen: (loomId: string) => void;
  /** The name, or a long-press anywhere on the row. */
  onName: (loomId: string) => void;
  onLongPress: (loomId: string) => void;
  onReopen: (loomId: string) => void;
  onStart: (loomId: string) => void;
  /** Looms whose people were confirmed or changed on this screen; the rest can't be saved yet. */
  confirmed: Set<string>;
  onConfirm: (loomId: string) => void;
  /** Parts to pick from when a row has none yet (a back-filled saree with no known starting part). */
  types?: { id: string }[];
  onPart?: (loomId: string, workTypeId: string) => void;
};

/** One loom per row: number, who and which saree, then inches (or Reopen / Start saree). */
const DailyList = (p: ListProps) => {
  const { t, lang } = useKT();
  const timer = useRef<number | undefined>(undefined);
  const fired = useRef(false);

  const press = (loomId: string) => ({
    onPointerDown: (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest("input,select")) return;
      fired.current = false;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        fired.current = true;
        p.onLongPress(loomId);
      }, LONG_PRESS_MS);
    },
    onPointerUp: () => window.clearTimeout(timer.current),
    onPointerLeave: () => window.clearTimeout(timer.current),
    onPointerCancel: () => window.clearTimeout(timer.current),
    onContextMenu: (e: React.MouseEvent) => {
      // The long-press already opened the actions; no browser menu on top of it.
      if (fired.current) e.preventDefault();
    },
  });
  // A click that ends a long-press must not also open the loom.
  const guard = (fn: () => void) => () => {
    if (fired.current) {
      fired.current = false;
      return;
    }
    fn();
  };

  // One number column for the whole list, wide enough for its longest code ("N101"), so names line up.
  const longest = p.rows.reduce((n, r) => Math.max(n, String(r.loomCode).length), 1);
  const loomWidth = `${Math.max(30, longest * 12 + 4)}px`;
  return (
    <ul className="kd-list" style={{ "--kd-loom-w": loomWidth } as React.CSSProperties}>
      {p.rows.map((r) => {
        const line = p.lines.get(r.loomId);
        const saved = r.kind === "RUN" && line?.status === "SAVED";
        const on = p.selectedId === r.loomId;
        const unconfirmed = needsPeople(r, line, p.confirmed);
        return (
          <li
            key={r.loomId}
            className={`kd-row is-${r.kind}${on ? " is-on" : ""}${saved ? " is-saved" : ""}${unconfirmed ? " is-unconfirmed" : ""}${unconfirmed && r.helperChanged ? " is-helper-changed" : ""}`}
            {...press(r.loomId)}
          >
            <button type="button" className="kd-row__loom" onClick={guard(() => p.onOpen(r.loomId))} aria-label={`${t("Open loom")} ${r.loomCode}`}>
              {r.loomCode}
            </button>
            <button type="button" className="kd-row__main" onClick={guard(() => p.onName(r.loomId))}>
              {r.kind === "RUN" ? (
                <>
                  <strong>
                    {(() => {
                      const w = (line && p.personOf?.(line.weaverId)) || r.weaver;
                      return w ? `${w.code} ${w.name || ""}`.trim() : <span className="kd-err">{t("Choose the weaver")}</span>;
                    })()}
                  </strong>
                  {r.startMove && !saved ? (
                    <span className="kd-move">
                      {t("Saree {{n}} · starts {{from}} → will start {{to}}", { n: r.sareeNumber, from: dayMonth(r.startMove.currentStart, lang), to: dayMonth(p.date, lang) })}
                      {r.startMove.needsApproval ? ` · ${t("goes to Approvals")}` : ""}
                    </span>
                  ) : <span>
                    {t("Saree")} {r.sareeNumber}
                    {r.daysSince
                      ? ` · ${r.daysSince === 1 ? t("last entry yesterday") : t("last entry {{n}} days ago", { n: r.daysSince })}`
                      : `${r.designNumber ? ` · ${r.designNumber}` : ""}${line ? ` · ${p.partName(line.workTypeId)}` : ""}`}
                  </span>}
                </>
              ) : r.kind === "CLOSED" ? (
                <>
                  <strong>
                    {t("Closed")} · {t(reasonLabel(r.reason))}
                  </strong>
                  <span>
                    {r.closedSince && r.closedSince < p.date ? t("since {{day}}", { day: fmtDay(r.closedSince) }) : t("today")} · {t("counts as updated")}
                  </span>
                </>
              ) : (
                r.moveStart ? (
                  <>
                    <strong>{t("Saree {{n}} from {{day}}", { n: r.moveStart.sareeNumber, day: fmtDay(r.moveStart.currentStart) })}</strong>
                    <span>
                      {r.moveStart.canMove
                        ? t("Move its start to this day to enter work")
                        : t("Ask the office to move saree {{n}}'s start", { n: r.moveStart.sareeNumber })}
                    </span>
                  </>
                ) : (
                  <>
                    <strong>{t("No saree")}</strong>
                    <span>{t("Start one to enter work")}</span>
                  </>
                )
              )}
            </button>
            <div className="kd-row__end">
              {r.kind === "RUN" && line ? (
                saved ? (
                  <span className="kd-inch is-saved" aria-label={`${t("Saved")} ${line.inches} ${t("in")}`}>
                    {line.inches}
                  </span>
                ) : (
                  <input
                    ref={(el) => p.registerInput(r.loomId, el)}
                    className={`kd-inch${line.status === "ERROR" ? " is-err" : ""}${r.daysSince && r.daysSince >= 4 ? " is-stale" : ""}`}
                    inputMode="decimal"
                    enterKeyHint="next"
                    placeholder={t("in")}
                    value={line.inches}
                    disabled={line.status === "SAVING"}
                    aria-label={`${t("Inches")} ${t("Loom")} ${r.loomCode}`}
                    onChange={(e) => p.onType(r.loomId, e.target.value.replace(/[^\d.]/g, ""))}
                    onPaste={(e) => {
                      if (p.onPaste(r.loomId, e.clipboardData.getData("text"))) e.preventDefault();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        p.onEnter(r.loomId);
                      }
                    }}
                  />
                )
              ) : r.kind === "CLOSED" ? (
                <button type="button" className="kd-btn kd-btn--line kd-btn--sm" onClick={() => p.onReopen(r.loomId)}>
                  {t("Reopen")}
                </button>
              ) : (
                <button type="button" className="kd-btn kd-btn--sm" onClick={() => p.onStart(r.loomId)}>
                  {r.moveStart ? (r.moveStart.canMove ? t("Move start") : t("Open loom")) : t("Start saree")}
                </button>
              )}
            </div>
            {line?.message && (line.status === "ERROR" || line.status === "SAVED") ? <p className="kd-row__err">{line.message}</p> : null}
            {r.kind === "RUN" && needsPart(line) && p.onPart ? (
              <div className="kd-row__confirm">
                <label htmlFor={`kd-part-${r.loomId}`}>{t("Which part? Pick it before saving.")}</label>
                <select
                  id={`kd-part-${r.loomId}`}
                  className="kd-part-pick"
                  value=""
                  onChange={(e) => e.target.value && p.onPart?.(r.loomId, e.target.value)}
                >
                  <option value="">{t("Choose the part")}</option>
                  {(p.types || []).map((w) => (
                    <option key={w.id} value={w.id}>
                      {p.partName(w.id)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {unconfirmed ? (
              <div className="kd-row__confirm">
                <span>{r.helperChanged ? t("The helper changed this week. Check before saving.") : t("Same people as last time?")}</span>
                <button type="button" className="kd-btn kd-btn--line kd-btn--sm" onClick={() => p.onConfirm(r.loomId)}>
                  {t("Same as yesterday ✓")}
                </button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export default DailyList;
